import {
  create,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import type { Member, Invitation } from '../../types/domain';
import { USER_API_BASE_URL } from './config';

type OrganizationMember = {
  user_id: string;
  name: string;
  email: string;
};

type Role = {
  id?: string;
  name?: string;
  description?: string;
};

type MemberRoles = {
  user_id: string;
  roles: Role[];
};

const ADMIN_ROLE_NAME = 'OrganizationAdmin';

const axiosInstance = create({
  baseURL: USER_API_BASE_URL,
});

let accessTokenProvider: (() => string | Promise<string>) | null = null;

axiosInstance.interceptors.request.use(
  async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    if (!accessTokenProvider) {
      throw new Error('API client is not initialized.');
    }
    const accessToken = await accessTokenProvider();
    config.headers['Authorization'] = `Bearer ${accessToken}`;
    return config;
  }
);

const initializeUserApiClient = (
  provider: () => string | Promise<string>
): void => {
  accessTokenProvider = provider;
};

const userApiClient = {
  getOrganizationMembers: async (): Promise<Member[]> => {
    const members = await axiosInstance
      .get('/organization-member')
      .then(
        (
          response: AxiosResponse<{ items: OrganizationMember[] }>
        ): OrganizationMember[] => {
          return response.data.items;
        }
      );
    if (members.length === 0) {
      return [];
    }

    const userIds = members.map((member: OrganizationMember): string => {
      return member.user_id;
    });
    const adminFlags = await axiosInstance
      .post('/organization-member/member-roles', { user_ids: userIds })
      .then(
        (
          response: AxiosResponse<{ items: MemberRoles[] }>
        ): Map<string, boolean> => {
          return new Map(
            response.data.items.map(
              (memberRole: MemberRoles): [string, boolean] => {
                const hasAdminRole = memberRole.roles.some(
                  (role: Role): boolean => {
                    return role.name === ADMIN_ROLE_NAME;
                  }
                );
                return [memberRole.user_id, hasAdminRole];
              }
            )
          );
        }
      );

    return members.map((member: OrganizationMember): Member => ({
      ...member,
      is_admin: adminFlags.get(member.user_id) ?? false,
    }));
  },
  updateMember: async (
    userId: string,
    changes: { name: string }
  ): Promise<void> => {
    await axiosInstance.post(`/organization-member/${userId}`, changes);
  },
  removeMember: async (userId: string): Promise<void> => {
    await axiosInstance.delete(`/organization-member/${userId}`);
  },
  getInvitations: async (): Promise<Invitation[]> => {
    return axiosInstance
      .get('/organization-member/invitation')
      .then(
        (response: AxiosResponse<{ items: Invitation[] }>): Invitation[] => {
          return response.data.items;
        }
      );
  },
  createInvitation: async (data: {
    inviterName: string;
    email: string;
    is_admin?: boolean;
  }): Promise<Invitation> => {
    return axiosInstance
      .post('/organization-member/invitation', data)
      .then((response: AxiosResponse<Invitation>): Invitation => {
        return response.data;
      });
  },
  deleteInvitation: async (invitationId: string): Promise<void> => {
    await axiosInstance.delete(
      `/organization-member/invitation/${invitationId}`
    );
  },
};

export { userApiClient, initializeUserApiClient };
